import React, { useState } from "react";
import {
  sendSignInLink,
  login,
  register,
  sendPasswordReset,
} from "../../firebase";

interface EmailSignInProps {
  onSignInSuccess?: () => void;
  onPasswordSignInSuccess?: () => void;
  onShowRegister?: () => void;
}

const EmailSignIn: React.FC<EmailSignInProps> = ({
  onSignInSuccess,
  onPasswordSignInSuccess,
  onShowRegister,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState<"magic" | "password">("password");
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleAuthModeChange = (mode: "magic" | "password") => {
    setAuthMode(mode);
    setShowCreateAccount(false);
    setAlert({ ...alert, open: false });
    if (mode === "magic") {
      setPassword("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setAlert({
        open: true,
        message: "Please enter your email address",
        severity: "error",
      });
      return;
    }

    if (authMode === "password" && !password) {
      setAlert({
        open: true,
        message: "Please enter your password",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      if (authMode === "magic") {
        await sendSignInLink(email);
        setAlert({
          open: true,
          message: `A sign-in link has been sent to ${email}. Please check your email.`,
          severity: "success",
        });
        if (onSignInSuccess) {
          onSignInSuccess();
        }
      } else {
        // Try to sign in with email/password
        try {
          await login(email, password);
          setAlert({
            open: true,
            message: "Successfully signed in!",
            severity: "success",
          });

          window.location.href = "/portal";
        } catch (loginError: any) {
          // If login fails due to user not found, offer to create account
          if (
            loginError.code === "auth/user-not-found" ||
            loginError.code === "auth/invalid-credential"
          ) {
            setShowCreateAccount(false);
            setAlert({
              open: true,
              message:
                "Email or password invalid, please try again or reset your password.",
              severity: "error",
            });
          } else {
            throw loginError;
          }
        }
      }
    } catch (error: any) {
      setAlert({
        open: true,
        message:
          error.message ||
          `Failed to ${
            authMode === "magic" ? "send sign-in link" : "sign in"
          }. Please try again.`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!email || !password) {
      setAlert({
        open: true,
        message: "Please enter both email and password",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      await register(email, password, { email });
      setAlert({
        open: true,
        message: "Account created successfully! You are now signed in.",
        severity: "success",
      });
      setShowCreateAccount(false);
      if (onPasswordSignInSuccess) {
        onPasswordSignInSuccess();
      } else {
        // Default behavior: redirect to portal after a short delay
        setTimeout(() => {
          window.location.href = "/portal";
        }, 1500);
      }
    } catch (error: any) {
      setAlert({
        open: true,
        message: error.message || "Failed to create account. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setAlert({
        open: true,
        message: "Please enter your email address first",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      await sendPasswordReset(email);
      setAlert({
        open: true,
        message: `Password reset email sent to ${email}. Check your inbox.`,
        severity: "success",
      });
    } catch (error: any) {
      setAlert({
        open: true,
        message: error.message || "Failed to send password reset email",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <div className="p-6 w-full max-w-md mx-auto bg-white rounded-lg shadow-md">
      <h2 className="text-base font-semibold leading-7 text-gold text-center">
        Athlete Portal
      </h2>
      <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
        Sign in
      </h2>

      {/* Auth Mode Switcher */}
      {/* <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
        <button
          type="button"
          onClick={() => handleAuthModeChange("password")}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            authMode === "password"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Email & Password
        </button>
        <button
          type="button"
          onClick={() => handleAuthModeChange("magic")}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            authMode === "magic"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Magic Link
        </button>
      </div> */}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="mb-4">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold"
            required
            autoComplete="email"
          />
        </div>

        {authMode === "password" && (
          <div className="mb-4">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold"
              required={authMode === "password"}
              autoComplete="current-password"
            />
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gold hover:bg-gold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
            loading ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          {loading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {authMode === "magic" ? "Sending Link..." : "Signing In..."}
            </>
          ) : authMode === "magic" ? (
            "Send Magic Link"
          ) : (
            "Sign In"
          )}
        </button>

        {showCreateAccount && authMode === "password" && (
          <button
            type="button"
            onClick={handleCreateAccount}
            disabled={loading}
            className={`w-full flex justify-center py-3 px-4 border border-gold rounded-md shadow-sm text-sm font-medium text-gold bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Creating Account..." : "Create New Account"}
          </button>
        )}

        {authMode === "password" && !showCreateAccount && (
          <div className="text-center">
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={loading}
              className="text-sm text-gray-500 hover:text-gray-500 focus:outline-none focus:underline"
            >
              Forgot your password?
            </button>
          </div>
        )}

        {/* Create Account Link */}
        <div className="text-center pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onShowRegister}
            className="text-sm font-medium bg-goldlight p-2 w-full text-gold hover:text-gold focus:outline-none focus:underline"
          >
            Don't have an account? <strong>Create Account</strong>
          </button>
        </div>

        {authMode === "magic" && (
          <>
            <p className="text-center text-sm text-gray-600 mb-4">
              We'll email you a magic link for a password-free sign in.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg mb-3 space-y-2">
              <h3 className="text-sm font-bold text-gray-800 mb-2">
                Using a mobile device?
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-xs text-gray-700">
                    For the best experience, open the magic link in your
                    device's web browser instead of your email app
                  </span>
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-xs text-gray-700">
                    If you click the link in Gmail, use the 'Open with...'
                    option and select your browser
                  </span>
                </li>
              </ul>
            </div>
          </>
        )}
      </form>

      {alert.open && (
        <div
          className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 w-full max-w-sm ${
            alert.severity === "error"
              ? "bg-red-100 border-red-400 text-red-700"
              : "bg-green-100 border-green-400 text-green-700"
          } border px-4 py-3 rounded`}
          role="alert"
        >
          <div className="flex justify-between items-center">
            <span className="block sm:inline">{alert.message}</span>
            <button onClick={handleCloseAlert} className="ml-4">
              <svg
                className={`h-6 w-6 ${
                  alert.severity === "error" ? "text-red-700" : "text-green-700"
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailSignIn;
